import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pw220aApi, F21A0, F21A1 } from '../../../services/pw220aApi';

const EMPTY_MASTER: F21A0 = {
  f21a0_01: '',
  f21a0_07: '',
  f21a0_10: '',
  f21a0_11: null,
  f21a0_12: '',
  f21a0_13: '',
  f21a0_14: '',
  f21a0_15: '',
  f21a0_16: 0,
  f21a0_17: 0,
  f21a0_18: '',
  f21a0_19: 0,
  f21a0_20: 0,
  f21a0_21: 0,
  f21a0_23: 0,
  f21a0_24: 0,
  f21a0_25: 0,
  f21a0_26: 0,
  f21a0_30: '',
  f21a0_31: ' ',
  f21a0_33: '',
  f21a0_34: null,
  f21a0_80: 'N',
  f21a0_81: '',
  f21a0_82: null,
  f21a0_90: 0,
  f21a0_91: '',
  f21a0_92: null,
  f21a0_93: '',
};

function makeEmptyDetail(orderId: string, seq: number): F21A1 {
  return {
    f21a1_01: orderId,
    f21a1_02: seq,
    f21a1_07: '',
    f21a1_11: '',
    f21a1_11a: '',
    f21a1_12: '',
    f21a1_13: 0,
    f21a1_14: 0,
    f21a1_15: 0,
    f21a1_16: 0,
    f21a1_17: 0,
    f21a1_18: '',
    f21a1_19: 0,
    f21a1_21: '',
    f21a1_22: 0,
    f21a1_90: 0,
    f21a1_91: '',
    f21a1_92: null,
    f21a1_93: '',
  };
}

export function usePW220ARecord() {
  const [record, setRecord] = useState<F21A0 | null>(null);
  const [formData, setFormData] = useState<F21A0>({ ...EMPTY_MASTER });
  const [details, setDetails] = useState<F21A1[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const data = await pw220aApi.getByID(id);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details ?? []);
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    const today = new Date().toISOString().substring(0, 10) + 'T00:00:00Z';
    setFormData({ ...EMPTY_MASTER, f21a0_11: today });
    // Pre-populate 20 empty detail rows (BR-F01)
    const rows: F21A1[] = [];
    for (let i = 1; i <= 20; i++) {
      rows.push(makeEmptyDetail('', i));
    }
    setDetails(rows);
  }, []);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record });
      setDetails(record.details ?? []);
    } else {
      resetForAdd();
    }
  }, [record, resetForAdd]);

  const getField = useCallback(
    <K extends keyof F21A0>(key: K): F21A0[K] => formData[key],
    [formData]
  );

  const setField = useCallback(
    <K extends keyof F21A0>(key: K, value: F21A0[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSave = useCallback(
    async (isAdd: boolean, onSuccess: (saved: F21A0) => void) => {
      setLoading(true);
      try {
        // Filter out completely empty detail rows (no product code)
        const filteredDetails = details.filter(
          (d) => d.f21a1_11.trim() !== '' || d.f21a1_13 !== 0
        );
        const master = { ...formData };
        let saved: F21A0;
        if (isAdd) {
          saved = await pw220aApi.create({ master, details: filteredDetails });
        } else {
          saved = await pw220aApi.update(master.f21a0_01, { master, details: filteredDetails });
        }
        setRecord(saved);
        setFormData({ ...saved });
        setDetails(saved.details ?? []);
        message.success('存檔成功');
        onSuccess(saved);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { errors?: string[]; message?: string } } };
        const errors = e?.response?.data?.errors;
        if (errors && errors.length > 0) {
          message.error(errors.join(' / '));
        } else {
          message.error(e?.response?.data?.message ?? '存檔失敗');
        }
      } finally {
        setLoading(false);
      }
    },
    [formData, details]
  );

  const handleDelete = useCallback(
    async (onSuccess: () => void) => {
      if (!record) return;
      setLoading(true);
      try {
        await pw220aApi.delete(record.f21a0_01);
        message.success('刪除成功');
        setRecord(null);
        setFormData({ ...EMPTY_MASTER });
        setDetails([]);
        onSuccess();
      } catch {
        message.error('刪除失敗');
      } finally {
        setLoading(false);
      }
    },
    [record]
  );

  // ---- F21A1 detail row helpers ----

  const addDetailRows = useCallback((count = 10) => {
    setDetails((prev) => {
      const maxSeq = prev.length > 0 ? Math.max(...prev.map((r) => r.f21a1_02)) : 0;
      const newRows: F21A1[] = [];
      for (let i = 1; i <= count; i++) {
        newRows.push(makeEmptyDetail(formData.f21a0_01, maxSeq + i));
      }
      return [...prev, ...newRows];
    });
  }, [formData.f21a0_01]);

  const deleteDetailRow = useCallback((idx: number) => {
    setDetails((prev) =>
      prev
        .filter((_, i) => i !== idx)
        .map((r, i) => ({ ...r, f21a1_02: i + 1 }))
    );
  }, []);

  const updateDetailRow = useCallback(
    <K extends keyof F21A1>(idx: number, key: K, value: F21A1[K]) => {
      setDetails((prev) =>
        prev.map((r, i) => {
          if (i !== idx) return r;
          const updated = { ...r, [key]: value };
          // Auto-calculate amount on qty or price change (BR-C01)
          if (key === 'f21a1_13' || key === 'f21a1_14') {
            const qty = key === 'f21a1_13' ? (value as number) : r.f21a1_13;
            const price = key === 'f21a1_14' ? (value as number) : r.f21a1_14;
            updated.f21a1_15 = Math.round(qty * price * 100) / 100;
          }
          return updated;
        })
      );
      // Also update total amount in formData (BR-C02)
      setDetails((prev) => {
        const total = prev.reduce((sum, r) => sum + r.f21a1_15, 0);
        setFormData((fm) => ({ ...fm, f21a0_17: Math.round(total * 100) / 100 }));
        return prev;
      });
    },
    []
  );

  return {
    record,
    formData,
    details,
    loading,
    loadRecord,
    resetForAdd,
    revertToRecord,
    getField,
    setField,
    handleSave,
    handleDelete,
    addDetailRows,
    deleteDetailRow,
    updateDetailRow,
  };
}
